import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import confetti from "canvas-confetti";
import api from "../lib/api";
import { Target, Trophy } from "lucide-react";

const YD = (yard) => 90 + yard * 8.2;

export function Field({ away, home, ballYard, children, label }) {
  return (
    <div className="card-surface rounded-xl p-2 overflow-hidden" data-testid="sim-field">
      <svg viewBox="0 0 1000 400" className="w-full h-auto block rounded-lg">
        <rect x="0" y="0" width="1000" height="400" fill="#123c1f" />
        {[...Array(10)].map((_, i) => (
          <rect key={i} x={YD(i * 10)} y="0" width="82" height="400" fill={i % 2 === 0 ? "#15522a" : "#196032"} />
        ))}
        <rect x="0" y="0" width="90" height="400" fill="#7f1424" />
        <rect x="910" y="0" width="90" height="400" fill="#1e3a8a" />
        <text x="45" y="200" fill="#ffffffcc" fontSize="34" fontWeight="900" textAnchor="middle" transform="rotate(-90 45 200)" style={{ letterSpacing: 6 }}>{away?.abbrev || "AWAY"}</text>
        <text x="955" y="200" fill="#ffffffcc" fontSize="34" fontWeight="900" textAnchor="middle" transform="rotate(90 955 200)" style={{ letterSpacing: 6 }}>{home?.abbrev || "HOME"}</text>
        {[...Array(11)].map((_, i) => (
          <line key={i} x1={YD(i * 10)} y1="0" x2={YD(i * 10)} y2="400" stroke="#ffffff" strokeOpacity={i === 0 || i === 10 ? 0.9 : 0.45} strokeWidth={i === 0 || i === 10 ? 4 : 2} />
        ))}
        {[10, 20, 30, 40, 50, 40, 30, 20, 10].map((n, i) => (
          <g key={i}>
            <text x={YD((i + 1) * 10)} y="52" fill="#ffffff88" fontSize="24" fontWeight="800" textAnchor="middle">{n}</text>
            <text x={YD((i + 1) * 10)} y="368" fill="#ffffff88" fontSize="24" fontWeight="800" textAnchor="middle">{n}</text>
          </g>
        ))}
        {[...Array(21)].map((_, i) => (
          <g key={i}>
            <line x1={YD(i * 5)} y1="150" x2={YD(i * 5)} y2="162" stroke="#ffffff55" strokeWidth="2" />
            <line x1={YD(i * 5)} y1="238" x2={YD(i * 5)} y2="250" stroke="#ffffff55" strokeWidth="2" />
          </g>
        ))}
        {ballYard != null && (
          <g style={{ transform: `translateX(${YD(ballYard)}px)`, transition: "transform 0.8s cubic-bezier(.4,1.4,.6,1)" }}>
            <ellipse cx="0" cy="200" rx="16" ry="10" fill="#8a4b1f" stroke="#fff" strokeWidth="1.5" />
            <line x1="-7" y1="200" x2="7" y2="200" stroke="#fff" strokeWidth="1.5" />
          </g>
        )}
        {children}
      </svg>
      {label && <div className="text-[10px] text-neutral-500 text-center pt-1.5 pb-0.5 font-data uppercase tracking-widest">{label}</div>}
    </div>
  );
}

function GameChips({ games, sel, onPick }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1" data-testid="sim-game-chips">
      {games.map((g) => (
        <button key={g.id} onClick={() => onPick(g)}
          className={`shrink-0 text-xs font-bold uppercase tracking-wide px-3 py-2 rounded-lg border transition-colors duration-200 ${sel?.id === g.id ? "bg-[#8b1526] border-[#c22040] text-white" : "border-white/15 text-neutral-400 hover:border-[#c22040]/60 hover:text-white"}`}>
          {g.away.abbrev} @ {g.home.abbrev}
        </button>
      ))}
    </div>
  );
}

function ballSpot(play) {
  if (!play) return 50;
  if (play.side === "away") return play.type === "TD" ? 104 : Math.max(100 - ((play.yards || 35) - 17), 55);
  return play.type === "TD" ? -4 : Math.min((play.yards || 35) - 17, 45);
}

function GameSim({ league, game }) {
  const [sim, setSim] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [playIdx, setPlayIdx] = useState(-1);
  const timer = useRef(null);
  useEffect(() => { setSim(null); setPlayIdx(-1); }, [game?.id]);
  useEffect(() => () => clearInterval(timer.current), []);
  const run = async () => {
    if (!game) return;
    setSpinning(true);
    setSim(null);
    setPlayIdx(-1);
    clearInterval(timer.current);
    try {
      const { data } = await api.get(`/football/sim/game?league=${league}&event=${game.id}`);
      setSpinning(false);
      setSim(data);
      if (!data.plays?.length) { setPlayIdx(0); confetti({ particleCount: 90, spread: 70, origin: { y: 0.55 } }); return; }
      let i = -1;
      timer.current = setInterval(() => {
        i += 1;
        setPlayIdx(i);
        if (i >= data.plays.length - 1) {
          clearInterval(timer.current);
          confetti({ particleCount: 110, spread: 75, origin: { y: 0.55 } });
        }
      }, 1100);
    } catch { setSpinning(false); }
  };
  const done = sim && playIdx >= (sim.plays?.length || 0) - 1;
  const cur = sim?.plays?.[Math.max(playIdx, 0)];
  const shown = sim?.plays?.slice(0, playIdx + 1) || [];
  return (
    <div className="space-y-4">
      {game && (
        <button onClick={run} disabled={spinning || (sim && !done)} data-testid="sim-game-run"
          className="bg-[#a855f7] hover:bg-[#9333ea] disabled:opacity-50 text-black font-black uppercase tracking-wider text-xs px-5 py-2.5 rounded transition-colors duration-200">
          {spinning ? "Kicking off..." : sim && !done ? "Game in progress..." : sim ? "Run It Back" : `Simulate ${game.away.abbrev} @ ${game.home.abbrev}`}
        </button>
      )}
      {(spinning || sim) && game && (
        <div className="space-y-3">
          <div className="card-surface rounded-xl p-3 flex items-center justify-center gap-4 font-data" data-testid="sim-live-score">
            {game.away.logo && <img src={game.away.logo} alt="" className="w-8 h-8 object-contain" />}
            <span className="font-display font-bold uppercase text-sm">{game.away.abbrev}</span>
            <span className="text-2xl font-black gold-text">{cur ? cur.away_score : 0}</span>
            <span className="text-neutral-600">—</span>
            <span className="text-2xl font-black gold-text">{cur ? cur.home_score : 0}</span>
            <span className="font-display font-bold uppercase text-sm">{game.home.abbrev}</span>
            {game.home.logo && <img src={game.home.logo} alt="" className="w-8 h-8 object-contain" />}
            {sim && !done && cur && <span className="text-[10px] uppercase tracking-widest text-red-400 font-black animate-pulse ml-2">● {cur.q}</span>}
            {done && <span className="text-[10px] uppercase tracking-widest text-[#22c55e] font-black ml-2">Final{sim.ot ? " / OT" : ""}</span>}
          </div>
          <Field away={game.away} home={game.home} ballYard={sim ? ballSpot(cur) : 50}
            label={cur ? `${cur.q} · ${cur.team} — ${cur.desc}` : "Teams are in the tunnel..."} />
        </div>
      )}
      {shown.length > 0 && (
        <div className="card-surface rounded-xl divide-y divide-white/5 max-h-56 overflow-y-auto" data-testid="sim-play-log">
          {[...shown].reverse().map((p, i) => (
            <div key={shown.length - i} className="flex items-center gap-2.5 px-3 py-2 text-sm">
              <span className="font-data text-[10px] text-neutral-500 w-7 shrink-0">{p.q}</span>
              {p.logo && <img src={p.logo} alt="" className="w-5 h-5 object-contain" loading="lazy" />}
              <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${p.type === "TD" ? "bg-[#a855f7] text-black" : "bg-white/10 text-neutral-300"}`}>{p.type}</span>
              <span className="flex-1 text-neutral-300 truncate">{p.desc}</span>
              <span className="font-data text-xs text-neutral-500 shrink-0">{p.away_score}-{p.home_score}</span>
            </div>
          ))}
        </div>
      )}
      {done && (
        <div className="space-y-3 fade-up-1" data-testid="sim-game-result">
          <div className="card-surface rounded-xl overflow-hidden">
            <table className="w-full text-sm font-data text-center">
              <thead><tr className="text-neutral-500 text-[10px] uppercase tracking-wider border-b border-white/10">
                <th className="p-2.5 text-left">Team</th>
                {sim.quarters.map((q) => <th key={q.q} className="p-2.5">{q.q === "OT" ? "OT" : `Q${q.q}`}</th>)}
                <th className="p-2.5 text-[#a855f7]">Final</th></tr></thead>
              <tbody>
                {["away", "home"].map((side) => (
                  <tr key={side} className="border-b border-white/5 last:border-0">
                    <td className="p-2.5 text-left font-sans font-bold uppercase">{sim[side].abbrev}</td>
                    {sim.quarters.map((q) => <td key={q.q} className="p-2.5">{q[side]}</td>)}
                    <td className={`p-2.5 font-black ${sim.winner === sim[side].name ? "text-[#22c55e]" : "text-neutral-400"}`}>{side === "away" ? sim.final_away : sim.final_home}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="card-surface rounded-xl p-4 text-center border border-[#22c55e]/30">
            <div className="font-chalk text-3xl"><Trophy size={22} className="inline text-[#a855f7] mr-2 -mt-1" /><span className="gold-text">{sim.winner}</span> <span className="text-neutral-300">win it {sim.final_away}-{sim.final_home}{sim.ot ? " in OT!" : "!"}</span></div>
            <div className="text-[11px] text-neutral-500 mt-1 font-data">Market expected {sim.exp.away} - {sim.exp.home} · run it back?</div>
          </div>
          <p className="text-[10px] text-neutral-600">{sim.note}</p>
        </div>
      )}
    </div>
  );
}

function TDSim({ league, game }) {
  const [sim, setSim] = useState(null);
  const [spinning, setSpinning] = useState(false);
  useEffect(() => { setSim(null); }, [game?.id]);
  const run = async () => {
    if (!game) return;
    setSpinning(true);
    setSim(null);
    try {
      const { data } = await api.get(`/football/sim/td?league=${league}&event=${game.id}`);
      setTimeout(() => {
        setSim(data);
        setSpinning(false);
        if (data.total_tds > 0) confetti({ particleCount: 24 * Math.min(data.total_tds, 5), spread: 75, origin: { y: 0.55 } });
      }, 900);
    } catch { setSpinning(false); }
  };
  const zone = (scorers, homeSide) =>
    scorers.slice(0, 4).map((p, i) => (
      <g key={p.name}>
        <clipPath id={`clip-${homeSide ? "h" : "a"}-${i}`}><circle cx={homeSide ? 45 : 955} cy={70 + i * 88} r="26" /></clipPath>
        <circle cx={homeSide ? 45 : 955} cy={70 + i * 88} r="28" fill="#000" stroke="#a855f7" strokeWidth="3" />
        {p.headshot && <image href={p.headshot} x={(homeSide ? 45 : 955) - 26} y={70 + i * 88 - 26} width="52" height="52" clipPath={`url(#clip-${homeSide ? "h" : "a"}-${i})`} preserveAspectRatio="xMidYMid slice" />}
        {p.tds > 1 && <text x={(homeSide ? 45 : 955) + 20} y={70 + i * 88 - 20} fill="#a855f7" fontSize="18" fontWeight="900">x{p.tds}</text>}
      </g>
    ));
  return (
    <div className="space-y-4">
      {game && (
        <button onClick={run} disabled={spinning} data-testid="sim-td-run"
          className="bg-[#a855f7] hover:bg-[#9333ea] disabled:opacity-50 text-black font-black uppercase tracking-wider text-xs px-5 py-2.5 rounded transition-colors duration-200">
          {spinning ? "Rolling the end zone..." : `Simulate TD scorers — ${game.away.abbrev} @ ${game.home.abbrev}`}
        </button>
      )}
      {spinning && <div className="font-chalk text-2xl text-neutral-400 animate-pulse">Who finds paydirt tonight...</div>}
      {sim && (
        <div className="space-y-3 fade-up-1" data-testid="sim-td-result">
          <Field away={game.away} home={game.home}
            label={`${sim.total_tds} TD${sim.total_tds === 1 ? "" : "s"} in this sim — scorers shown in the end zone they attack`}>
            {zone(sim.teams[0]?.scorers || [], false)}
            {zone(sim.teams[1]?.scorers || [], true)}
          </Field>
          <div className="grid md:grid-cols-2 gap-4">
            {sim.teams.map((t) => (
              <div key={t.team} className="card-surface rounded-xl overflow-hidden">
                <div className="bg-[#8b1526] border-b border-[#c22040]/40 px-3 py-2 flex items-center gap-2">
                  {t.logo && <img src={t.logo} alt="" className="w-6 h-6 object-contain" />}
                  <span className="text-xs font-black uppercase tracking-widest text-white">{t.team}</span>
                  <span className="ml-auto text-[10px] text-red-100/80 font-data">implied {t.implied} pts</span>
                </div>
                {t.scorers.length === 0 && <div className="p-4 text-sm text-neutral-500">No TDs in this sim — field goals only.</div>}
                {t.scorers.map((p) => (
                  <div key={p.name} className="flex items-center gap-2.5 px-3 py-2 border-b border-white/5 last:border-0">
                    {p.headshot ? <img src={p.headshot} alt="" className="w-8 h-8 rounded-full object-cover bg-white/5" loading="lazy" /> : <span className="w-8 h-8 rounded-full bg-white/5 inline-flex items-center justify-center"><Target size={13} className="text-neutral-600" /></span>}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{p.name}</div>
                      <div className="text-[10px] text-neutral-500">{p.pos}</div>
                    </div>
                    <span className="font-data text-sm font-black gold-text">{p.tds > 1 ? `${p.tds} TDs 🔥` : "TD"}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="card-surface rounded-xl p-4 text-center">
            <div className="font-chalk text-2xl">{sim.total_tds > 0 ? <span><span className="gold-text">{sim.total_tds} touchdowns</span> in this sim — run it back?</span> : <span className="text-neutral-400">Defensive slugfest — zero TDs this sim.</span>}</div>
          </div>
          <p className="text-[10px] text-neutral-600">{sim.note}</p>
        </div>
      )}
    </div>
  );
}

export default function Simulators({ league }) {
  const { data } = useQuery({ queryKey: [league, "board"], queryFn: () => api.get(`/${league}/board`).then((r) => r.data), staleTime: 15 * 60 * 1000 });
  const games = (data?.days || []).flatMap((d) => d.games).filter((g) => g.state === "pre").slice(0, 20);
  const [mode, setMode] = useState("game");
  const [game, setGame] = useState(null);
  useEffect(() => { if (!game && games.length > 0) setGame(games[0]); }, [games, game]);
  return (
    <div className="space-y-4">
      <p className="text-neutral-500 text-sm">Play the game before it happens. Game Sim runs a full 60 minutes off the live market line — watch the ball move play by play; TD Sim rolls every skill player's touchdown odds. Different result every run.</p>
      <div className="flex gap-2" data-testid="sim-mode-tabs">
        {[{ key: "game", label: "Game Sim" }, { key: "td", label: "TD Sim" }].map((m) => (
          <button key={m.key} onClick={() => setMode(m.key)} data-testid={`sim-mode-${m.key}`}
            className={`text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full border transition-colors duration-200 ${mode === m.key ? "bg-[#a855f7] text-black border-[#a855f7]" : "border-white/15 text-neutral-400 hover:border-[#a855f7]/60 hover:text-[#a855f7]"}`}>
            {m.label}
          </button>
        ))}
      </div>
      {games.length === 0 && <div className="card-surface rounded-xl p-10 text-center text-neutral-400">No upcoming games to simulate — check back when the slate posts.</div>}
      <GameChips games={games} sel={game} onPick={setGame} />
      {mode === "game" ? <GameSim league={league} game={game} /> : <TDSim league={league} game={game} />}
    </div>
  );
}
